import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import './MealPlanStats.css';

const MealPlanStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    fetchMealPlanStats();
  }, []);

  const fetchMealPlanStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/meal-plan-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch meal plan statistics');
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching meal plan stats:', err);
      setError(err.message);
      toast.error('Failed to load meal plan statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMealTypeIcon = (mealType) => {
    const icons = {
      breakfast: '🌅',
      lunch: '☀️',
      dinner: '🌙',
      snack: '🍎'
    };
    return icons[mealType] || '🍽️';
  };

  const getMealTypeColor = (mealType) => {
    const colors = {
      breakfast: '#f59e0b',
      lunch: '#10b981',
      dinner: '#6366f1',
      snack: '#ec4899'
    };
    return colors[mealType] || '#6b7280';
  };

  if (loading) {
    return (
      <div className="meal-plan-stats loading-state">
        <div className="loading-spinner"></div>
        <p>Loading meal plan statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="meal-plan-stats error-state">
        <p>{error}</p>
        <button onClick={fetchMealPlanStats} className="retry-button">
          <i className="fas fa-redo"></i> Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const { summary, mealTypeDistribution, recentMealPlans, topUsersByMeals } = stats;

  return (
    <div className="meal-plan-stats">
      <div className="stats-header">
        <h2>
          <i className="fas fa-calendar-alt"></i> Meal Plan Statistics
        </h2>
        <button className="refresh-button" onClick={fetchMealPlanStats}>
          <i className="fas fa-sync-alt"></i> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="stats-summary-grid">
        <div className="stat-card primary">
          <div className="stat-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-content">
            <h3>{summary.totalUsersWithMealPlans}</h3>
            <p>Users with Meal Plans</p>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">
            <i className="fas fa-clipboard-list"></i>
          </div>
          <div className="stat-content">
            <h3>{summary.totalMealPlans}</h3>
            <p>Total Meal Plans</p>
          </div>
        </div>

        <div className="stat-card info">
          <div className="stat-icon">
            <i className="fas fa-utensils"></i>
          </div>
          <div className="stat-content">
            <h3>{summary.totalMeals}</h3>
            <p>Total Meals Planned</p>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">
            <i className="fas fa-share-alt"></i>
          </div>
          <div className="stat-content">
            <h3>{summary.sharedMealPlans}</h3>
            <p>Shared Meal Plans</p>
          </div>
        </div>

        <div className="stat-card secondary">
          <div className="stat-icon">
            <i className="fas fa-chart-bar"></i>
          </div>
          <div className="stat-content">
            <h3>{summary.avgMealsPerPlan}</h3>
            <p>Avg Meals per Plan</p>
          </div>
        </div>

        <div className="stat-card accent">
          <div className="stat-icon">
            <i className="fas fa-trophy"></i>
          </div>
          <div className="stat-content">
            <h3>{summary.maxMeals}</h3>
            <p>Most Meals in a Plan</p>
          </div>
        </div>
      </div>

      {/* Meal Type Distribution */}
      <div className="stats-section">
        <h3 className="section-header">
          <i className="fas fa-pie-chart"></i> Meal Type Distribution
        </h3>
        <div className="meal-type-distribution">
          {mealTypeDistribution.map((type) => (
            <div key={type._id} className="meal-type-bar">
              <div className="meal-type-label">
                <span className="meal-type-icon">{getMealTypeIcon(type._id)}</span>
                <span className="meal-type-name">{type._id}</span>
              </div>
              <div className="meal-type-bar-container">
                <div 
                  className="meal-type-bar-fill"
                  style={{ 
                    width: `${(type.count / Math.max(...mealTypeDistribution.map(t => t.count))) * 100}%`,
                    backgroundColor: getMealTypeColor(type._id)
                  }}
                ></div>
              </div>
              <span className="meal-type-count">{type.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Users by Meals */}
      <div className="stats-section">
        <h3 className="section-header">
          <i className="fas fa-medal"></i> Top Users by Meals Planned
        </h3>
        <div className="top-users-list">
          <table className="stats-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>User</th>
                <th>Email</th>
                <th>Meals</th>
                <th>Shared</th>
              </tr>
            </thead>
            <tbody>
              {topUsersByMeals.map((user, index) => (
                <tr key={user._id}>
                  <td>
                    <span className={`rank-badge rank-${index + 1}`}>
                      {index < 3 ? ['🥇', '🥈', '🥉'][index] : `#${index + 1}`}
                    </span>
                  </td>
                  <td>{user.userName}</td>
                  <td className="email-cell">{user.userEmail}</td>
                  <td>
                    <span className="meal-count-badge">{user.mealCount}</span>
                  </td>
                  <td>
                    {user.shareEnabled ? (
                      <span className="shared-badge yes">
                        <i className="fas fa-check"></i> Yes
                      </span>
                    ) : (
                      <span className="shared-badge no">
                        <i className="fas fa-times"></i> No
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Meal Plans */}
      <div className="stats-section">
        <h3 className="section-header">
          <i className="fas fa-clock"></i> Recent Meal Plan Activity
        </h3>
        <div className="recent-plans-list">
          <table className="stats-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Meals</th>
                <th>Shared</th>
                <th>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {recentMealPlans.map((plan) => (
                <tr key={plan._id}>
                  <td>{plan.userName}</td>
                  <td className="email-cell">{plan.userEmail}</td>
                  <td>
                    <span className="meal-count-badge">{plan.mealCount}</span>
                  </td>
                  <td>
                    {plan.shareEnabled ? (
                      <span className="shared-badge yes">
                        <i className="fas fa-check"></i> Yes
                      </span>
                    ) : (
                      <span className="shared-badge no">
                        <i className="fas fa-times"></i> No
                      </span>
                    )}
                  </td>
                  <td className="date-cell">{formatDate(plan.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MealPlanStats;



